import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatCurrency } from '../services/api';
import { formatDateDisplay } from '../lib/dateUtils';

export default function CollectionMemoList() {
  const navigate = useNavigate();
  const [memos, setMemos] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/collection-memos', { params: { search: search || undefined, limit: 50 } })
      .then(r => setMemos(r.data.memos || []))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection memo?')) return;
    try { await api.delete(`/collection-memos/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="collection-memo-list">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-bold text-2xl text-slate-900">Collection Memos</h1><p className="text-sm text-slate-500">Transport freight collection management</p></div>
        <button onClick={() => navigate('/collection-memos/new')} className="btn-accent flex items-center gap-2" data-testid="new-cm-btn"><Plus className="w-4 h-4" /> New Collection Memo</button>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1"><Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search memo #, party, truck..." className="form-input pl-9 w-full" data-testid="search-cm" /></div>
        <button onClick={load} className="btn-secondary">Search</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase text-slate-600"><th className="p-3 text-left">S.No.</th><th className="p-3 text-left">Memo #</th><th className="p-3 text-left">Date</th><th className="p-3 text-left">Party</th><th className="p-3 text-left">Truck</th><th className="p-3 text-left">Route</th><th className="p-3 text-right">Freight</th><th className="p-3 text-right">Balance</th><th className="p-3"></th></tr></thead>
          <tbody>{loading ? <tr><td colSpan={9} className="p-8 text-center text-slate-400">Loading...</td></tr> :
            memos.length === 0 ? <tr><td colSpan={9} className="p-8 text-center text-slate-400">No collection memos yet</td></tr> :
            memos.map((m, idx) => (
              <tr key={m.memo_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/collection-memos/${m.memo_id}`)}>
                <td className="p-3 text-slate-400">{idx + 1}</td>
                <td className="p-3 font-mono font-medium text-blue-600">{m.memo_number}</td>
                <td className="p-3 font-mono text-xs">{formatDateDisplay(m.date)}</td>
                <td className="p-3 font-medium">{m.party_name}</td>
                <td className="p-3 font-mono">{m.truck_no}</td>
                <td className="p-3 text-sm">{m.from_location} &rarr; {m.to_location}</td>
                <td className="p-3 text-right font-mono">{formatCurrency(m.freight)}</td>
                <td className="p-3 text-right font-mono font-medium text-green-600">{formatCurrency(m.balance)}</td>
                <td className="p-3" onClick={e => e.stopPropagation()}><button onClick={() => handleDelete(m.memo_id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}</tbody>
        </table>
      </div>
    </div>
  );
}
